/**
 * GA4 video engagement tracking for the PDP product gallery.
 * YouTube only. Intentional plays only.
 *
 * Handles two video paths:
 *   1. VideoModal (#VideoHolder) — opened when user clicks a YouTube link trigger.
 *      Always intentional; tracked unconditionally.
 *   2. Product gallery inline player (.product__video[data-video-type="youtube"]) —
 *      only tracked when data-video-style="unmuted".
 *
 * Both paths dispatch a 'youtubeStateChange' CustomEvent (via assets/theme.js).
 * This module uses document-level delegation to catch both.
 *
 * Events fired:
 *   - pdp_video_started  — first intentional play per modal session
 *   - pdp_video_progress — at 10, 25, 50, 75, 100% milestones
 */

const MILESTONES = [10, 25, 50, 75, 100];
const POLL_INTERVAL_MS = 500;

/** @type {Map<string, { fired: Set, progressInterval: number|null }>} */
const trackedVideos = new Map();

/**
 * @param {string} key
 * @returns {{ fired: Set, progressInterval: number|null }}
 */
const getTracker = (key) => {
  if (!trackedVideos.has(key)) {
    trackedVideos.set(key, { fired: new Set(), progressInterval: null });
  }
  return trackedVideos.get(key);
};

/**
 * @param {string} eventName
 * @param {Object} params
 */
const fireGtag = (eventName, params) => {
  if (typeof gtag !== 'function') return;
  gtag('event', eventName, params);
};

/**
 * @param {string} trackId
 * @param {string} youtubeVideoId
 * @returns {Object}
 */
const buildBaseParams = (trackId, youtubeVideoId) => ({
  video_id: trackId,
  video_type: 'youtube',
  youtube_video_id: youtubeVideoId,
  product_url: window.location.pathname,
  page_location: window.location.href,
});

// Reset modal tracker on close so a reopened modal counts as a new session
document.addEventListener('modalClose.VideoModal', () => {
  const tracker = trackedVideos.get('VideoHolder');
  if (tracker) {
    clearInterval(tracker.progressInterval);
    trackedVideos.delete('VideoHolder');
  }
});

// Document-level delegation — catches events from both VideoModal and product gallery
document.addEventListener('youtubeStateChange', (e) => {
  const { state, player, youtubeVideoId, source } = e.detail;
  const target = e.target;

  // For product gallery players: only track unmuted/intentional plays
  if (source !== 'modal' && target.dataset?.videoStyle !== 'unmuted') return;

  const trackId = target.id || 'VideoHolder';
  const videoId = youtubeVideoId || target.dataset?.youtubeId || '';
  const tracker = getTracker(trackId);
  const baseParams = buildBaseParams(trackId, videoId);

  if (state === 1) { // playing
    if (!tracker.fired.has('started')) {
      tracker.fired.add('started');
      fireGtag('pdp_video_started', baseParams);
    }

    clearInterval(tracker.progressInterval);
    tracker.progressInterval = setInterval(() => {
      const duration = player?.videoPlayer?.getDuration?.();
      const current = player?.videoPlayer?.getCurrentTime?.();
      if (!duration) return;

      const pct = (current / duration) * 100;
      for (const milestone of MILESTONES) {
        if (pct >= milestone && !tracker.fired.has(milestone)) {
          tracker.fired.add(milestone);
          fireGtag('pdp_video_progress', { ...baseParams, percent_watched: milestone });
        }
      }
    }, POLL_INTERVAL_MS);
  }

  if (state === 0 || state === 2) { // ended or paused
    clearInterval(tracker.progressInterval);
  }
});
